/**
 * Gunboat (GDD §3.6: pointed hull with red stripe, windowed cabin, rotating radar, bow gun, V wake when moving).
 * Ported verbatim (concept L462–L485).
 */
import {
  BoxGeometry,
  CylinderGeometry,
  ExtrudeGeometry,
  Group,
  Mesh,
  PlaneGeometry,
  Shape,
  type Material,
} from 'three';
import { mesh } from '../../world/geometry.ts';
import type { Materials } from '../materials.ts';

export interface GunboatParts {
  radar: Group;
  wake: Mesh;
  bow: Mesh;
}

export function buildGunboat(M: Materials, wakeMat: Material): Group & { userData: GunboatParts } {
  const g = new Group();
  const s = new Shape();
  s.moveTo(0, -8.6);
  s.quadraticCurveTo(2.75, -5, 2.75, -1);
  s.lineTo(2.55, 7.5);
  s.lineTo(-2.55, 7.5);
  s.lineTo(-2.75, -1);
  s.quadraticCurveTo(-2.75, -5, 0, -8.6);
  const hull = new ExtrudeGeometry(s, {
    depth: 2.4,
    bevelEnabled: true,
    bevelThickness: 0.12,
    bevelSize: 0.12,
    bevelSegments: 2,
    curveSegments: 10,
  });
  hull.rotateX(Math.PI / 2);
  hull.translate(0, 1.4, 0);
  mesh(hull, M.hull, 0, 0, 0, g);
  const deck = new ExtrudeGeometry(s, { depth: 0.12, bevelEnabled: false, curveSegments: 10 });
  deck.scale(0.94, 0.94, 1);
  deck.rotateX(Math.PI / 2);
  deck.translate(0, 1.56, 0);
  mesh(deck, M.deck, 0, 0, 0, g);
  const stripe = new ExtrudeGeometry(s, { depth: 0.28, bevelEnabled: false, curveSegments: 10 });
  stripe.scale(1.03, 1.03, 1);
  stripe.rotateX(Math.PI / 2);
  stripe.translate(0, 0.9, 0);
  mesh(stripe, M.red, 0, 0, 0, g);
  mesh(new BoxGeometry(3.3, 2.0, 5.0), M.cabin, 0, 2.55, 1.3, g);
  mesh(new BoxGeometry(3.36, 0.55, 4.6), M.glass, 0, 3.05, 1.2, g, false);
  mesh(new BoxGeometry(2.9, 0.9, 2.2), M.cabin, 0, 4.0, 1.8, g);
  mesh(new BoxGeometry(2.96, 0.36, 2.0), M.glass, 0, 4.1, 1.7, g, false);
  mesh(new CylinderGeometry(0.07, 0.09, 3.0, 8), M.dark, 0, 5.9, 2.1, g);
  const radar = new Group();
  radar.position.set(0, 6.9, 2.1);
  g.add(radar);
  mesh(new BoxGeometry(1.9, 0.12, 0.26), M.dark, 0, 0, 0, radar);
  mesh(new CylinderGeometry(0.62, 0.72, 0.5, 16), M.dark, 0, 1.8, -4.3, g);
  const barrel = new CylinderGeometry(0.1, 0.12, 2.6, 10);
  barrel.rotateX(Math.PI / 2);
  mesh(barrel, M.dark, 0, 2.0, -5.6, g);
  for (let i = 0; i < 5; i++)
    mesh(new BoxGeometry(0.5, 0.5, 0.5), M.deck, (i % 2 ? 1 : -1) * 1.4, 1.85, 4.6 + (i > 2 ? 1.2 : 0), g);
  const wake = new Mesh(new PlaneGeometry(30, 64), wakeMat);
  wake.rotation.x = -Math.PI / 2;
  wake.position.set(0, 0.07, 7.5 + 30);
  g.add(wake);
  const bow = new Mesh(new PlaneGeometry(9, 12), wakeMat);
  bow.rotation.x = -Math.PI / 2;
  bow.position.set(0, 0.08, -4);
  g.add(bow);
  g.userData = { radar, wake, bow } satisfies GunboatParts;
  return g as Group & { userData: GunboatParts };
}
